import { Injectable, signal, computed, inject } from "@angular/core";
import { HttpClient, HttpParams, HttpErrorResponse } from "@angular/common/http";
import {
  BehaviorSubject,
  Subject,
  Observable,
  of,
  from,
  delay,
  map,
  catchError,
  tap,
  switchMap,
  throwError,
  mergeMap,
  toArray,
} from "rxjs";
import {
  DataDictionary,
  DictionaryField,
  HierarchyNode,
  HierarchySidebarSelection,
  SampleDataRow,
} from "../models/data-dictionary.model";
import { environment } from "../../../environments/environment";
import { MockToggleService } from "./mock-toggle.service";
import {
  MOCK_HIERARCHY,
  MOCK_DICTIONARIES,
  generateMockId,
} from "./mock-data";
import { fieldCodeToApi, fieldCodeToDisplay, normalizeMasterCodeForMatch } from "../utils/field-code-format.util";
import { MasterTablesService } from "./master-tables.service";

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface FieldCreateRequest {
  position: number;
  subPosition: number;
  fieldName: string;
  dataTypeId: number;
  description?: string;
}

@Injectable({
  providedIn: "root",
})
export class DataDictionaryService {
  private http = inject(HttpClient);
  private mockToggle = inject(MockToggleService);
  private masterTablesService = inject(MasterTablesService);
  private hierarchyData$ = new BehaviorSubject<HierarchyNode[]>([]);
  private dictionariesData$ = new BehaviorSubject<DataDictionary[]>([]);
  private selectedDictionaryId = signal<number | null>(null);
  private searchTerm = signal<string>("");
  private loading = signal<boolean>(false);
  private hierarchyLoaded = false;
  private dictionaryChanged$ = new Subject<number>();

  readonly hierarchy$ = this.hierarchyData$.asObservable();
  readonly onDictionaryChanged$ = this.dictionaryChanged$.asObservable();
  readonly dictionaries$ = this.dictionariesData$.asObservable();
  readonly isLoading = computed(() => this.loading());
  readonly currentSearchTerm = computed(() => this.searchTerm());

  private useMock(): boolean {
    return this.mockToggle.isMockEnabled();
  }

  private initMockData(): void {
    const raw = JSON.parse(JSON.stringify(MOCK_HIERARCHY)) as HierarchyNode[];
    this.hierarchyData$.next(this.normalizeHierarchy(raw));
    this.dictionariesData$.next(JSON.parse(JSON.stringify(MOCK_DICTIONARIES)));
    this.hierarchyLoaded = true;
  }

  getHierarchy(): Observable<HierarchyNode[]> {
    if (this.hierarchyLoaded && this.hierarchyData$.value.length > 0) {
      return of(this.hierarchyData$.value);
    }

    this.loading.set(true);

    if (this.useMock()) {
      this.initMockData();
      this.hierarchyLoaded = true;
      this.loading.set(false);
      return of(this.hierarchyData$.value);
    }

    return this.http
      .get<HierarchyNode[] | { hierarchy: HierarchyNode[] }>(`${environment.apiUrl}/hierarchy`)
      .pipe(
        map((data) => {
          const nodes = Array.isArray(data) ? data : (data as { hierarchy: HierarchyNode[] }).hierarchy;
          return this.normalizeHierarchy(nodes);
        }),
        tap((data) => {
          this.hierarchyData$.next(data);
          this.hierarchyLoaded = true;
          this.loading.set(false);
        }),
        catchError((error) => {
          console.error("Error fetching hierarchy from API:", error);
          this.loading.set(false);
          return of([]);
        }),
      );
  }

  private normalizeHierarchy(nodes: HierarchyNode[]): HierarchyNode[] {
    return nodes.map(node => this.normalizeNode(node));
  }

  private normalizeNode(node: HierarchyNode): HierarchyNode {
    // Expansion is client-only. API defaults new folders to expanded=True; ignore for UI.
    const normalized: HierarchyNode = {
      id: node.id,
      name: node.name,
      type: node.type,
      parentId: node.parentId,
      order: node.order,
      expanded: node.type === 'category' ? false : undefined
    };

    if (node.children && Array.isArray(node.children) && node.children.length > 0) {
      normalized.children = node.children.map(child => this.normalizeNode(child));
    } else if (node.type !== 'dictionary') {
      normalized.children = [];
    }

    return normalized;
  }

  refreshHierarchy(): Observable<HierarchyNode[]> {
    if (this.useMock()) {
      // In mock, keep the current in-memory tree. Re-running init would reset to MOCK_HIERARCHY
      // and undo deletes/moves the user has applied.
      if (this.hierarchyData$.value.length === 0) {
        this.initMockData();
      } else {
        this.hierarchyLoaded = true;
      }
      return of(this.hierarchyData$.value);
    }
    this.hierarchyLoaded = false;
    return this.getHierarchy();
  }

  private normalizeFieldFromApi(field: DictionaryField): DictionaryField {
    return {
      ...field,
      dataType: fieldCodeToDisplay(field.dataType),
    };
  }

  private normalizeDictionary(dictionary: DataDictionary): DataDictionary {
    return {
      ...dictionary,
      createdAt: dictionary.createdAt ? new Date(dictionary.createdAt) : new Date(),
      updatedAt: dictionary.updatedAt ? new Date(dictionary.updatedAt) : new Date(),
      fields: (dictionary.fields || []).map((f) => this.normalizeFieldFromApi(f)),
      sampleData: dictionary.sampleData || [],
    };
  }

  private mapPartialFieldToApi(updates: Partial<DictionaryField>): Record<string, unknown> {
    // Backend expects FK ids (dataTypeId). Do not send code fields
    // like dataType, even if they exist in the UI model.
    const out: Record<string, unknown> = {};

    if (updates.position !== undefined) out["position"] = updates.position;
    if (updates.subPosition !== undefined) out["subPosition"] = updates.subPosition;
    if (updates.fieldName !== undefined) out["fieldName"] = updates.fieldName;
    if (updates.dataTypeId !== undefined) out["dataTypeId"] = updates.dataTypeId;
    if (updates.description !== undefined) out["description"] = updates.description;

    return out;
  }

  getDictionaries(): Observable<DataDictionary[]> {
    if (this.useMock()) {
      this.initMockData();
      return of(this.dictionariesData$.value);
    }

    return this.http
      .get<DataDictionary[]>(`${environment.apiUrl}/dictionaries`)
      .pipe(
        map((response) => response.map((d) => this.normalizeDictionary(d))),
        tap((dicts) => this.dictionariesData$.next(dicts)),
        catchError((error) => {
          console.error("Error fetching dictionaries from API:", error);
          return of([]);
        }),
      );
  }

  getDictionaryById(
    id: number,
    fieldsPage?: number,
    fieldsPageSize?: number,
  ): Observable<DataDictionary | undefined> {
    if (this.useMock()) {
      this.initMockData();
      const dict = this.dictionariesData$.value.find(d => d.id === id);
      return of(dict);
    }

    const params: Record<string, string> = {};
    if (fieldsPage !== undefined) {
      params["page"] = String(fieldsPage);
    }
    if (fieldsPageSize !== undefined) {
      params["pageSize"] = String(fieldsPageSize);
    }

    return this.http
      .get<DataDictionary>(`${environment.apiUrl}/dictionaries/${String(id)}`, {
        params,
      })
      .pipe(
        map((data) => this.normalizeDictionary(data)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return of(undefined);
          }
          console.error(`Error fetching dictionary ${id} from API:`, error);
          return throwError(() => error);
        }),
      );
  }

  getDictionariesByParent(parentId: number): Observable<DataDictionary[]> {
    if (this.useMock()) {
      this.initMockData();
      const dicts = this.dictionariesData$.value
        .filter(d => d.parentId === parentId)
        .sort((a, b) => a.name.localeCompare(b.name));
      return of(dicts);
    }

    return this.http
      .get<DataDictionary[]>(`${environment.apiUrl}/dictionaries`, {
        params: {
          parentId,
        },
      })
      .pipe(
        map((response) =>
          response
            .map((d) => this.normalizeDictionary(d))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ),
        catchError((error) => {
          console.error("Error fetching dictionaries by parent from API:", error);
          return of([]);
        }),
      );
  }

  getDictionariesByParentPaginated(
    parentId: number,
    page = 1,
    pageSize = 20,
  ): Observable<PaginatedResponse<DataDictionary>> {
    if (this.useMock()) {
      this.initMockData();
      const all = this.dictionariesData$.value
        .filter((d) => d.parentId === parentId)
        .sort((a, b) => a.name.localeCompare(b.name));
      const total = all.length;
      const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
      const start = (page - 1) * pageSize;
      const items = all.slice(start, start + pageSize);

      return of({
        items,
        page,
        pageSize,
        total,
        totalPages,
      });
    }

    return this.getDictionariesByParent(parentId)
      .pipe(
        map((all) => {
          const total = all.length;
          const totalPages = total > 0 ? Math.ceil(total / pageSize) : 0;
          const boundedPage = totalPages > 0 ? Math.min(Math.max(page, 1), totalPages) : 1;
          const start = (boundedPage - 1) * pageSize;
          const items = all.slice(start, start + pageSize);

          return {
            items,
            page: boundedPage,
            pageSize,
            total,
            totalPages,
          };
        }),
        catchError((error) => {
          console.error("Error fetching paginated dictionaries by parent from API:", error);
          return of({
            items: [],
            page,
            pageSize,
            total: 0,
            totalPages: 0,
          });
        }),
      );
  }

  searchDictionaries(term: string): Observable<DataDictionary[]> {
    this.searchTerm.set(term);
    return this.getDictionaries().pipe(
      map((dicts) => {
        if (!term) return dicts;
        const lowerTerm = term.toLowerCase();
        return dicts.filter(
          (d) =>
            d.name.toLowerCase().includes(lowerTerm) ||
            d.description.toLowerCase().includes(lowerTerm) ||
            d.fields.some((f) => f.fieldName.toLowerCase().includes(lowerTerm)),
        );
      }),
    );
  }

  createDictionary(
    dictionary: Omit<DataDictionary, "id" | "createdAt" | "updatedAt">,
  ): Observable<DataDictionary> {
    if (this.useMock()) {
      const newDict: DataDictionary = {
        ...dictionary,
        id: generateMockId('dict-'),
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const current = this.dictionariesData$.value;
      this.dictionariesData$.next([...current, newDict]);
      return of(newDict);
    }

    return this.http
      .post<DataDictionary>(`${environment.apiUrl}/dictionaries`, dictionary)
      .pipe(
        map((data) => this.normalizeDictionary(data)),
        tap((newDict) => {
          const current = this.dictionariesData$.value;
          this.dictionariesData$.next([...current, newDict]);
        }),
        catchError((error) => {
          console.error("Error creating dictionary via API:", error);
          return throwError(() => error);
        }),
      );
  }

  updateDictionary(
    id: number,
    updates: Partial<DataDictionary>,
  ): Observable<DataDictionary> {
    if (this.useMock()) {
      return this.mockUpdateDictionary(id, updates);
    }

    return this.http
      .put<DataDictionary>(`${environment.apiUrl}/dictionaries/${id}`, updates)
      .pipe(
        map((data) => this.normalizeDictionary(data)),
        tap((updated) => {
          const current = this.dictionariesData$.value;
          const index = current.findIndex((d) => d.id === id);
          if (index !== -1) {
            const newList = [...current];
            newList[index] = updated;
            this.dictionariesData$.next(newList);
          }
          if (updates.name !== undefined) {
            this.syncDictionaryNameInHierarchy(updated.id, updated.name);
          }
        }),
        catchError((error) => {
          console.error("Error updating dictionary via API:", error);
          return throwError(() => error);
        }),
      );
  }

  private mockUpdateDictionary(id: number, updates: Partial<DataDictionary>): Observable<DataDictionary> {
    const current = this.dictionariesData$.value;
    const index = current.findIndex((d) => d.id === id);
    if (index === -1) {
      return of({} as DataDictionary);
    }
    const updated = { ...current[index], ...updates, updatedAt: new Date() };
    const newList = [...current];
    newList[index] = updated;
    this.dictionariesData$.next(newList);
    if (updates.name !== undefined) {
      this.syncDictionaryNameInHierarchy(updated.id, updated.name);
    }
    return of(updated);
  }

  deleteDictionary(id: number): Observable<boolean> {
    if (this.useMock()) {
      const current = this.dictionariesData$.value;
      this.dictionariesData$.next(current.filter((d) => d.id !== id));
      this.removeDictionaryFromHierarchy(id);
      return of(true);
    }

    return this.http
      .delete<{ success: boolean }>(`${environment.apiUrl}/dictionaries/${id}`)
      .pipe(
        map(() => true),
        tap(() => {
          const current = this.dictionariesData$.value;
          this.dictionariesData$.next(current.filter((d) => d.id !== id));
          this.removeDictionaryFromHierarchy(id);
        }),
        catchError((error) => {
          console.error("Error deleting dictionary via API:", error);
          return throwError(() => error);
        }),
      );
  }

  bulkDeleteDictionaries(ids: number[]): Observable<{ succeeded: number[]; failed: number[] }> {
    return from(ids).pipe(
      mergeMap(
        (id) =>
          this.deleteDictionary(id).pipe(
            map(() => ({ id, success: true as const })),
            catchError(() => of({ id, success: false as const })),
          ),
        3,
      ),
      toArray(),
      map((results) => ({
        succeeded: results.filter((r) => r.success).map((r) => r.id),
        failed: results.filter((r) => !r.success).map((r) => r.id),
      })),
    );
  }

  addField(
    dictionaryId: number,
    field: FieldCreateRequest,
    ): Observable<DictionaryField> {
    if (this.useMock()) {
      return this.mockAddField(dictionaryId, field);
    }

    const payload: Record<string, unknown> = {
      position: field.position,
      subPosition: field.subPosition,
      fieldName: field.fieldName,
      dataTypeId: field.dataTypeId,
      description: field.description ?? '',
    };

    return this.http
      .post<DictionaryField>(`${environment.apiUrl}/dictionaries/${dictionaryId}/fields`, payload)
      .pipe(
        map((newField) => this.normalizeFieldFromApi(newField)),
        tap((newField) => {
          this.updateDictFields(dictionaryId, fields => [...fields, newField]);
        }),
        catchError((error) => {
          console.error("Error adding field via API:", error);
          return throwError(() => error);
        }),
      );
  }

  private mockAddField(dictionaryId: number, field: FieldCreateRequest): Observable<DictionaryField> {
    const newField: DictionaryField = {
      id: generateMockId('field-'),
      position: field.position,
      subPosition: field.subPosition,
      fieldName: field.fieldName,
      dataTypeId: field.dataTypeId,
      dataType: 'TEXT',
      description: field.description ?? '',
    };
    this.updateDictFields(dictionaryId, fields => [...fields, newField]);
    return of(newField);
  }

  updateField(
    dictionaryId: number,
    fieldId: number,
    updates: Partial<DictionaryField>,
  ): Observable<DictionaryField> {
    if (this.useMock()) {
      return this.mockUpdateField(dictionaryId, fieldId, updates);
    }

    const needsDataTypeId = updates.dataTypeId === undefined && typeof updates.dataType === 'string' && updates.dataType.trim() !== '';

    const resolveFkIdsIfNeeded = (): Observable<Partial<DictionaryField>> => {
      if (!needsDataTypeId) {
        return of(updates);
      }

      return this.masterTablesService.getDataTypes().pipe(
        map((dataTypes) => {
          const patched: Partial<DictionaryField> = { ...updates };

          if (needsDataTypeId) {
            const input = normalizeMasterCodeForMatch(fieldCodeToDisplay(String(updates.dataType ?? '')).trim());
            const match = dataTypes.find((d: { id: number; code: string }) =>
              normalizeMasterCodeForMatch(String(d.code ?? "")) === input,
            );
            if (match) {
              patched.dataTypeId = match.id;
            }
          }

          return patched;
        }),
      );
    };

    return resolveFkIdsIfNeeded().pipe(
      switchMap((resolved) =>
        this.http.put<DictionaryField>(
          `${environment.apiUrl}/dictionaries/${dictionaryId}/fields/${fieldId}`,
          this.mapPartialFieldToApi(resolved),
        )
      ),
      map((updatedField) => this.normalizeFieldFromApi(updatedField)),
      tap((updatedField) => {
        this.updateDictFields(dictionaryId, (fields) =>
          fields.map((f) => (f.id === fieldId ? updatedField : f)),
        );
      }),
      catchError((error) => {
        console.error("Error updating field via API:", error);
        return throwError(() => error);
      }),
    );
  }

  private mockUpdateField(dictionaryId: number, fieldId: number, updates: Partial<DictionaryField>): Observable<DictionaryField> {
    const current = this.dictionariesData$.value;
    const dict = current.find(d => d.id === dictionaryId);
    const field = dict?.fields.find(f => f.id === fieldId);
    if (!field) return of({} as DictionaryField);
    const updatedField = { ...field, ...updates };
    this.updateDictFields(dictionaryId, fields =>
      fields.map(f => f.id === fieldId ? updatedField : f)
    );
    return of(updatedField);
  }

  deleteField(dictionaryId: number, fieldId: number): Observable<boolean> {
    if (this.useMock()) {
      this.updateDictFields(dictionaryId, fields => fields.filter(f => f.id !== fieldId));
      return of(true);
    }

    return this.http
      .delete<{ success: boolean }>(`${environment.apiUrl}/dictionaries/${dictionaryId}/fields/${fieldId}`)
      .pipe(
        map(() => true),
        tap(() => {
          this.updateDictFields(dictionaryId, fields => fields.filter(f => f.id !== fieldId));
        }),
        catchError((error) => {
          console.error("Error deleting field via API:", error);
          return throwError(() => error);
        }),
      );
  }

  private updateDictFields(dictionaryId: number, updater: (fields: DictionaryField[]) => DictionaryField[]): void {
    const current = this.dictionariesData$.value;
    const dictIndex = current.findIndex(d => d.id === dictionaryId);
    if (dictIndex !== -1) {
      const updated = { ...current[dictIndex] };
      updated.fields = updater(updated.fields);
      updated.updatedAt = new Date();
      const newList = [...current];
      newList[dictIndex] = updated;
      this.dictionariesData$.next(newList);
    }
  }

  createHierarchyNode(
    node: Omit<HierarchyNode, "id" | "order">,
  ): Observable<HierarchyNode> {
    const current = this.hierarchyData$.value;
    const siblings = node.parentId
      ? this.findChildren(current, node.parentId)
      : current;

    if (this.useMock()) {
      const newNode: HierarchyNode = {
        ...node,
        id: generateMockId(node.type === 'dictionary' ? 'dict-' : 'cat-'),
        order: siblings.length,
        children: node.type === 'category' ? [] : undefined,
      };
      if (node.parentId) {
        this.addChildToParent(current, node.parentId, newNode);
      } else {
        current.push(newNode);
      }
      this.hierarchyData$.next([...current]);
      return of(newNode);
    }

    const payload = {
      name: node.name,
      type: node.type,
      parentId: node.parentId || null,
      order: siblings.length,
    };

    return this.http
      .post<HierarchyNode>(`${environment.apiUrl}/hierarchy`, payload)
      .pipe(
        tap((newNode) => {
          if (node.parentId) {
            this.addChildToParent(current, node.parentId, newNode);
          } else {
            current.push(newNode);
          }
          this.hierarchyData$.next([...current]);
        }),
        catchError((error) => {
          console.error("Error creating hierarchy node via API:", error);
          return throwError(() => error);
        }),
      );
  }

  updateHierarchyNode(
    ref: HierarchySidebarSelection,
    updates: Partial<HierarchyNode>,
  ): Observable<HierarchyNode> {
    if (this.useMock()) {
      return this.mockUpdateHierarchyNode(ref, updates);
    }

    const params = new HttpParams().set("type", ref.type);
    return this.http
      .put<HierarchyNode>(`${environment.apiUrl}/hierarchy/${ref.id}`, updates, { params })
      .pipe(
        tap((updatedNode) => {
          const current = this.hierarchyData$.value;
          const node = this.findNode(current, updatedNode.id, updatedNode.type);
          if (node) {
            Object.assign(node, updatedNode);
            this.hierarchyData$.next([...current]);
          }
          if (updatedNode.type === "dictionary") {
            this.dictionaryChanged$.next(updatedNode.id);
          }
        }),
        catchError((error) => {
          console.error("Error updating hierarchy node via API:", error);
          return throwError(() => error);
        }),
      );
  }

  private mockUpdateHierarchyNode(
    ref: HierarchySidebarSelection,
    updates: Partial<HierarchyNode>,
  ): Observable<HierarchyNode> {
    const current = this.hierarchyData$.value;
    const node = this.findNode(current, ref.id, ref.type);
    if (node) {
      Object.assign(node, updates);
      this.hierarchyData$.next([...current]);
      if (node.type === "dictionary") {
        this.dictionaryChanged$.next(ref.id);
      }
      return of(node);
    }
    return of({} as HierarchyNode);
  }

  deleteHierarchyNode(ref: HierarchySidebarSelection): Observable<boolean> {
    if (this.useMock()) {
      const current = this.hierarchyData$.value;
      this.hierarchyData$.next(this.removeNode(current, ref.id, ref.type));
      return of(true);
    }

    const params = new HttpParams().set("type", ref.type);
    return this.http
      .delete<{ success: boolean }>(`${environment.apiUrl}/hierarchy/${ref.id}`, { params })
      .pipe(
        map(() => true),
        tap(() => {
          const current = this.hierarchyData$.value;
          const filtered = this.removeNode(current, ref.id, ref.type);
          this.hierarchyData$.next(filtered);
        }),
        catchError((error) => {
          console.error("Error deleting hierarchy node via API:", error);
          return throwError(() => error);
        }),
      );
  }

  reorderHierarchy(
    _parentId: number | null,
    nodeIds: number[],
  ): Observable<boolean> {
    const current = this.hierarchyData$.value;

    nodeIds.forEach((id, index) => {
      const node = this.findNode(current, id);
      if (node) {
        node.order = index;
      }
    });

    this.hierarchyData$.next([...current]);
    return of(true).pipe(delay(300));
  }

  /**
   * Depth in the tree. When `nodeType` is set, the starting node is resolved by id+type
   * (needed when folder and table reuse the same numeric id). Parent links are resolved as
   * categories, which matches this hierarchy model.
   */
  getNodeDepth(nodeId: number | null, nodeType?: HierarchyNode["type"]): number {
    if (!nodeId) return 0;

    const hierarchy = this.hierarchyData$.value;
    const first =
      nodeType !== undefined
        ? this.findNode(hierarchy, nodeId, nodeType)
        : this.findNode(hierarchy, nodeId);
    if (!first) return 0;

    let depth = 0;
    let parentId: number | null = first.parentId;
    while (parentId) {
      const parent = this.findNode(hierarchy, parentId, "category");
      if (!parent) break;
      depth++;
      parentId = parent.parentId;
    }

    return depth;
  }

  createFolderWithNode(
    parentId: number | null,
    name: string,
  ): Observable<HierarchyNode> {
    const depth = parentId ? this.getNodeDepth(parentId, "category") + 1 : 0;
    if (depth >= 2) {
      return new Observable((subscriber) => {
        subscriber.error(
          new Error(
            "Maximum hierarchy depth of 3 levels reached. Cannot create folder at this level.",
          ),
        );
      });
    }

    return this.createHierarchyNode({
      name,
      type: "category",
      parentId,
      expanded: false,
    });
  }

  createDictionaryWithNode(
    parentId: number | null,
    name: string,
    initialField?: {
      position: number;
      subPosition: number;
      fieldName: string;
      dataTypeId: number;
      description: string;
    },
  ): Observable<{ dictionary: DataDictionary; node: HierarchyNode }> {
    const fieldsForApi = initialField
      ? [
          {
            position: initialField.position,
            subPosition: initialField.subPosition,
            fieldName: initialField.fieldName,
            description: initialField.description ?? '',
            dataTypeId: initialField.dataTypeId,
          },
        ]
      : [];

    const dictionaryPayload = {
      name,
      description: '',
      parentId: parentId ?? null,
      fields: fieldsForApi,
      sampleData: [],
    };

    if (this.useMock()) {
      return this.mockCreateDictionaryWithNode(parentId, name, dictionaryPayload);
    }

    return this.http
      .post<DataDictionary>(`${environment.apiUrl}/dictionaries`, dictionaryPayload)
      .pipe(
        map((data) => this.normalizeDictionary(data)),
        switchMap((newDict) => {
          const currentDicts = this.dictionariesData$.value;
          this.dictionariesData$.next([...currentDicts, newDict]);

          const current = this.hierarchyData$.value;
          const siblings = parentId ? this.findChildren(current, parentId) : current;
          
          const newNode: HierarchyNode = {
            id: newDict.id,
            name: newDict.name,
            type: "dictionary",
            parentId,
            order: siblings.length,
          };

          if (parentId) {
            this.addChildToParent(current, parentId, newNode);
          } else {
            current.push(newNode);
          }
          this.hierarchyData$.next([...current]);

          return of({ dictionary: newDict, node: newNode });
        }),
        catchError((error) => {
          console.error("Error creating dictionary via API:", error);
          return throwError(() => error);
        }),
      );
  }

  private mockCreateDictionaryWithNode(
    parentId: number | null,
    name: string,
    payload: { name: string; description: string; parentId: number | null; fields: unknown[]; sampleData: SampleDataRow[] },
  ): Observable<{ dictionary: DataDictionary; node: HierarchyNode }> {
    const newId = generateMockId('dict-');
    const newDict: DataDictionary = {
      ...payload,
      id: newId,
      name,
      createdAt: new Date(),
      updatedAt: new Date(),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fields: (payload.fields || []).map((f: any) => ({ ...f, id: generateMockId('field-') })),
    };

    const currentDicts = this.dictionariesData$.value;
    this.dictionariesData$.next([...currentDicts, newDict]);

    const current = this.hierarchyData$.value;
    const siblings = parentId
      ? this.findChildren(current, parentId)
      : current;

    const newNode: HierarchyNode = {
      id: newId,
      name,
      type: "dictionary",
      parentId,
      order: siblings.length,
    };

    if (parentId) {
      this.addChildToParent(current, parentId, newNode);
    } else {
      current.push(newNode);
    }
    this.hierarchyData$.next([...current]);

    return of({ dictionary: newDict, node: newNode });
  }

  sortHierarchyAlphabetically(parentId: number | null): Observable<HierarchyNode[]> {
    const current = this.hierarchyData$.value;

    const sortRecursively = (nodes: HierarchyNode[]): HierarchyNode[] => {
      const sorted = [...nodes].sort((a, b) => a.name.localeCompare(b.name));
      return sorted.map((node, index) => ({
        ...node,
        order: index,
        children: node.children ? sortRecursively(node.children) : undefined,
      }));
    };

    if (parentId) {
      const updateChildren = (nodes: HierarchyNode[]): HierarchyNode[] => {
        return nodes.map((node) => {
          if (node.id === parentId && node.type === "category" && node.children) {
            return { ...node, children: sortRecursively(node.children) };
          }
          if (node.children) {
            return { ...node, children: updateChildren(node.children) };
          }
          return node;
        });
      };
      this.hierarchyData$.next(updateChildren(current));
    } else {
      this.hierarchyData$.next(sortRecursively(current));
    }

    return of(this.hierarchyData$.value);
  }

  moveHierarchyNode(
    nodeId: number,
    targetParentId: number | null,
    targetIndex?: number,
  ): Observable<boolean> {
    const current = this.hierarchyData$.value;
    const node = this.findNode(current, nodeId, "dictionary");

    if (!node) {
      return throwError(() => new Error("Node not found"));
    }

    if (node.type !== "dictionary") {
      return throwError(() => new Error("Only dictionary nodes can be moved"));
    }

    if (node.parentId === targetParentId) {
      return of(true);
    }

    if (targetParentId) {
      const targetParent = this.findNode(current, targetParentId, "category");
      if (!targetParent || targetParent.type !== "category") {
        return throwError(() => new Error("Target folder is invalid"));
      }
    }

    if (this.useMock()) {
      return this.mockMoveHierarchyNode(nodeId, targetParentId, targetIndex);
    }

    return this.http
      .put<DataDictionary>(`${environment.apiUrl}/dictionaries/${nodeId}`, {
        parentId: targetParentId,
        ...(typeof targetIndex === "number" ? { order: targetIndex } : {}),
      })
      .pipe(
        switchMap(() => this.refreshHierarchy()),
        map(() => true),
        catchError((error) => {
          console.error("Error moving dictionary node via API:", error);
          return throwError(() => error);
        }),
      );
  }

  importSampleData(
    dictionaryId: number,
    data: SampleDataRow[],
    mode: 'replace' | 'append' = 'replace',
  ): Observable<boolean> {
    if (this.useMock()) {
      return this.mockImportSampleData(dictionaryId, data, mode);
    }

    return this.http
      .post<{ success: boolean }>(
        `${environment.apiUrl}/dictionaries/${dictionaryId}/import/sample-data`,
        { mode, sampleData: data }
      )
      .pipe(
        map(() => true),
        tap(() => {
          this.updateDictSampleData(dictionaryId, data, mode);
        }),
        catchError((error) => {
          console.error("Error importing sample data via API:", error);
          return throwError(() => error);
        }),
      );
  }

  private mockImportSampleData(
    dictionaryId: number,
    data: SampleDataRow[],
    mode: 'replace' | 'append' = 'replace',
  ): Observable<boolean> {
    this.updateDictSampleData(dictionaryId, data, mode);
    return of(true);
  }

  private updateDictSampleData(
    dictionaryId: number,
    data: SampleDataRow[],
    mode: 'replace' | 'append' = 'replace',
  ): void {
    const current = this.dictionariesData$.value;
    const dictIndex = current.findIndex((d) => d.id === dictionaryId);
    if (dictIndex !== -1) {
      const existing = current[dictIndex]?.sampleData ?? [];
      const merged = mode === 'append' ? [...existing, ...data] : data;
      const updated = { ...current[dictIndex], sampleData: merged, updatedAt: new Date() };
      const newList = [...current];
      newList[dictIndex] = updated;
      this.dictionariesData$.next(newList);
    }
  }

  importDictionaryFields(
    dictionaryId: number,
    fieldsData: Record<string, string>[],
    mode: 'replace' | 'append' = 'replace',
  ): Observable<DataDictionary> {
    const apiMode = mode === 'append' ? 'merge' : 'replace';

    if (this.useMock()) {
      const mappedFields = fieldsData.map(row => this.mapCsvFieldToApiFieldLegacy(row));
      return this.mockImportDictionaryFields(dictionaryId, mappedFields, apiMode);
    }

    return this.masterTablesService.getDataTypes().pipe(
      map((dataTypes) => {
        const mappedFields = fieldsData.map((row) => this.mapCsvFieldToApiFieldFk(row, dataTypes));
        return mappedFields;
      }),
      switchMap((mappedFields) =>
        this.http.post<DataDictionary>(
          `${environment.apiUrl}/dictionaries/${dictionaryId}/import/fields`,
          { mode: apiMode, fields: mappedFields }
        )
      ),
      map((response) => this.normalizeDictionary(response)),
      tap((response) => {
        const current = this.dictionariesData$.value;
        const dictIndex = current.findIndex((d) => d.id === dictionaryId);
        if (dictIndex !== -1) {
          const newList = [...current];
          newList[dictIndex] = { ...response, updatedAt: new Date() };
          this.dictionariesData$.next(newList);
        }
      }),
      catchError((error) => {
        console.error("Error importing dictionary fields via API:", error);
        return throwError(() => error);
      }),
    );
  }

  private mockImportDictionaryFields(
    dictionaryId: number,
    mappedFields: Record<string, string>[],
    apiMode: 'replace' | 'merge' = 'replace',
  ): Observable<DataDictionary> {
    const current = this.dictionariesData$.value;
    const dictIndex = current.findIndex((d) => d.id === dictionaryId);
    if (dictIndex === -1) {
      return of({} as DataDictionary);
    }
    const dict = current[dictIndex];
    const existingFields = dict.fields ?? [];
    const normalizedImported: DictionaryField[] = mappedFields.map((row, index) => {
      return {
        id: generateMockId('field-'),
        position: index + 1,
        subPosition: 0,
        fieldName: row['fieldName'] || '',
        dataType: fieldCodeToDisplay(row['dataType'] || 'TEXT'),
        description: row['description'] || '',
      };
    });

    const newFields = apiMode === 'replace'
      ? normalizedImported
      : (() => {
          const byName = new Map<string, DictionaryField>(existingFields.map(f => [f.fieldName, f]));
          for (const importedField of normalizedImported) {
            const existing = byName.get(importedField.fieldName);
            if (existing) {
              byName.set(importedField.fieldName, { ...existing, ...importedField, id: existing.id });
            } else {
              byName.set(importedField.fieldName, importedField);
            }
          }
          return Array.from(byName.values());
        })();

    const updated = { ...dict, fields: newFields, updatedAt: new Date() };
    const newList = [...current];
    newList[dictIndex] = updated;
    this.dictionariesData$.next(newList);
    return of(updated);
  }

  private mapCsvFieldToApiFieldLegacy(csvRow: Record<string, string>): Record<string, string> {
    // Mock-only: keep existing behavior.
    const rawDt = (csvRow['Data Type'] || csvRow['data type'] || csvRow['DataType'] || 'TEXT').toUpperCase();
    return {
      fieldName: csvRow['Field Name'] || csvRow['field name'] || csvRow['FieldName'] || '',
      dataType: fieldCodeToApi(rawDt),
      description: csvRow['Description'] || csvRow['description'] || '',
    };
  }

  private mapCsvFieldToApiFieldFk(
    csvRow: Record<string, string>,
    dataTypes: { id: number; code: string }[],
  ): Record<string, unknown> {
    const rawDt = (csvRow['Data Type'] || csvRow['data type'] || csvRow['DataType'] || 'TEXT').trim();

    const normalizeDatatype = (v: string): string => {
      const normalized = normalizeMasterCodeForMatch(v || 'TEXT');
      // Accept common variants like "array" / "Array" / "ARRAY"
      return normalized;
    };

    const dtCode = normalizeDatatype(rawDt);

    const dt = dataTypes.find((d) => normalizeMasterCodeForMatch(d.code) === dtCode);
    if (!dt) {
      throw new Error(`Unknown data type "${rawDt}"`);
    }
    return {
      fieldName: csvRow['Field Name'] || csvRow['field name'] || csvRow['FieldName'] || '',
      dataTypeId: dt.id,
      description: csvRow['Description'] || csvRow['description'] || '',
    };
  }

  toggleNodeExpand(nodeId: number): void {
    const current = this.hierarchyData$.value;
    const node = this.findNode(current, nodeId, "category");
    if (node && node.type === "category") {
      node.expanded = !node.expanded;
      this.hierarchyData$.next([...current]);
    }
  }

  setSelectedDictionary(id: number | null): void {
    this.selectedDictionaryId.set(id);
  }

  setSearchTerm(term: string): void {
    this.searchTerm.set(term);
  }

  /** Keep sidebar tree label in sync when the table is renamed via /dictionaries. */
  private syncDictionaryNameInHierarchy(dictionaryId: number, name: string): void {
    const current = this.hierarchyData$.value;
    const treeNode = this.findNode(current, dictionaryId, "dictionary");
    if (!treeNode) {
      return;
    }
    treeNode.name = name;
    this.hierarchyData$.next([...current]);
  }

  /** Remove the table row from the sidebar tree after DELETE /dictionaries. */
  private removeDictionaryFromHierarchy(dictionaryId: number): void {
    const current = this.hierarchyData$.value;
    this.hierarchyData$.next(this.removeNode(current, dictionaryId, "dictionary"));
  }

  private findNode(
    nodes: HierarchyNode[],
    id: number,
    type?: HierarchyNode["type"],
  ): HierarchyNode | undefined {
    for (const node of nodes) {
      if (node.id === id && (type === undefined || node.type === type)) {
        return node;
      }
      if (node.children) {
        const found = this.findNode(node.children, id, type);
        if (found) return found;
      }
    }
    return undefined;
  }

  private findChildren(
    nodes: HierarchyNode[],
    parentId: number,
  ): HierarchyNode[] {
    const parent = this.findNode(nodes, parentId, "category");
    return parent?.children || [];
  }

  private removeNode(
    nodes: HierarchyNode[],
    id: number,
    type?: HierarchyNode["type"],
  ): HierarchyNode[] {
    return nodes
      .filter((n) => !(n.id === id && (type === undefined || n.type === type)))
      .map((n) => ({
        ...n,
        children: n.children ? this.removeNode(n.children, id, type) : undefined,
      }));
  }

  private addChildToParent(
    nodes: HierarchyNode[],
    parentId: number,
    child: HierarchyNode,
  ): void {
    const parent = this.findNode(nodes, parentId, "category");
    if (parent) {
      if (!parent.children) parent.children = [];
      parent.children.push(child);
    }
  }

  private mockMoveHierarchyNode(
    nodeId: number,
    targetParentId: number | null,
    targetIndex?: number,
  ): Observable<boolean> {
    const sourceNode = this.findNode(this.hierarchyData$.value, nodeId, "dictionary");
    const sourceParentId = sourceNode?.parentId ?? null;
    const sourceOrder = sourceNode?.order ?? 0;
    const hierarchyClone = JSON.parse(
      JSON.stringify(this.hierarchyData$.value),
    ) as HierarchyNode[];

    const movedNode = this.removeNodeById(hierarchyClone, nodeId, "dictionary");
    if (!movedNode) {
      return throwError(() => new Error("Node not found"));
    }

    movedNode.parentId = targetParentId;
    let normalizedTargetIndex = targetIndex;
    if (
      typeof normalizedTargetIndex === "number" &&
      sourceParentId === targetParentId &&
      normalizedTargetIndex > sourceOrder
    ) {
      normalizedTargetIndex = normalizedTargetIndex - 1;
    }

    if (targetParentId) {
      const targetParent = this.findNode(hierarchyClone, targetParentId, "category");
      if (!targetParent || targetParent.type !== "category") {
        return throwError(() => new Error("Target folder is invalid"));
      }
      if (!targetParent.children) {
        targetParent.children = [];
      }
      const insertionIndex = this.normalizeInsertionIndex(targetParent.children, normalizedTargetIndex);
      targetParent.children.splice(insertionIndex, 0, movedNode);
    } else {
      const insertionIndex = this.normalizeInsertionIndex(hierarchyClone, normalizedTargetIndex);
      hierarchyClone.splice(insertionIndex, 0, movedNode);
    }

    this.reindexNodeOrders(hierarchyClone);
    this.hierarchyData$.next(hierarchyClone);

    return of(true).pipe(delay(150));
  }

  private removeNodeById(
    nodes: HierarchyNode[],
    nodeId: number,
    type?: HierarchyNode["type"],
  ): HierarchyNode | null {
    for (let index = 0; index < nodes.length; index++) {
      const currentNode = nodes[index];
      if (currentNode.id === nodeId && (type === undefined || currentNode.type === type)) {
        const [removedNode] = nodes.splice(index, 1);
        return removedNode;
      }

      if (currentNode.children && currentNode.children.length > 0) {
        const removedNode = this.removeNodeById(currentNode.children, nodeId, type);
        if (removedNode) {
          return removedNode;
        }
      }
    }

    return null;
  }

  private reindexNodeOrders(nodes: HierarchyNode[]): void {
    nodes.forEach((node, index) => {
      node.order = index;
      if (node.children && node.children.length > 0) {
        this.reindexNodeOrders(node.children);
      }
    });
  }

  private normalizeInsertionIndex(nodes: HierarchyNode[], targetIndex?: number): number {
    if (typeof targetIndex !== "number" || Number.isNaN(targetIndex)) {
      return nodes.length;
    }
    if (targetIndex < 0) {
      return 0;
    }
    if (targetIndex > nodes.length) {
      return nodes.length;
    }
    return targetIndex;
  }
}
